import {
  FALLBACK_FAMILY, builtInWeights, matchSpec, snapWeight
} from '../core/fonts.js';

/* THE FONT REGISTRY — the runtime half.

   The table and the pure string building live in core/fonts.js. This is the part
   that touches document.fonts: deciding whether a face is usable right now,
   loading it, and registering uploads.

   The shape mirrors getTexture in scene/textures.js on purpose — return
   immediately, call back when ready, let the caller rebuild. An image that has
   not decoded yet is the same problem, and keeping the same shape is what saves
   every syncNode call site from having to become async. */

/* ── uploaded faces ──────────────────────────────────────────────────────────
   Registered into document.fonts at runtime and tracked here so opening a second
   project can drop the first one's faces. Without that, two projects that each
   ship a font called "Display" collide, and the second silently gets the first
   one's letterforms — the exact class of bug this module exists to prevent. */

const uploaded = new Map();          // family -> { family, weights:Set, faces:FontFace[] }

export const uploadedFamilies = () => [...uploaded.values()]
  .map(u => ({ family: u.family, weights: [...u.weights].sort((a, b) => a - b) }));

export const isUploaded = family => uploaded.has(family);

/** Weights available for a family, bundled or uploaded. */
export function weightsFor(family) {
  const b = builtInWeights(family);
  if (b) return b;
  const u = uploaded.get(family);
  return u ? [...u.weights].sort((a, b2) => a - b2) : [400];
}

/** Snap a weight to one the family actually has. */
export const snapFor = (family, weight) => snapWeight(weightsFor(family), weight);

/**
 * Register a face from font-file bytes — the upload path.
 * @returns {Promise<{family:string, weight:number}>}
 */
export async function registerFace(family, weight, buffer) {
  const w = +weight || 400;
  const face = new FontFace(family, buffer, { weight: String(w), style: 'normal' });
  await face.load();
  document.fonts.add(face);
  let u = uploaded.get(family);
  if (!u) { u = { family, weights: new Set(), faces: [] }; uploaded.set(family, u); }
  u.weights.add(w);
  u.faces.push(face);
  asked.add(family + '|' + w);       // handed over directly; there is nothing to load
  return { family, weight: w };
}

/** Drop every uploaded face. Run before registering a newly opened document's. */
export function clearDocFonts() {
  for (const u of uploaded.values()){
    for (const f of u.faces) document.fonts.delete(f);
    /* Forgotten as well as deleted. Left in `asked`, a family the NEXT project
       happens to name the same would be called ready while its own face was
       still being registered — which is the collision this function exists to
       prevent, arriving by a different door. */
    for (const w of u.weights) asked.delete(u.family + '|' + w);
  }
  uploaded.clear();
}

/* ── loading ─────────────────────────────────────────────────────────────── */

/**
 * Is this face usable for this text, right now?
 *
 * `text` matters. Each weight's CSS splits into unicode-range subsets, so a face
 * can be loaded for Latin and not yet for the accented characters someone just
 * typed. Passing the copy asks the question that actually matters, and means a
 * later subset triggers its own load without any cache bookkeeping.
 */
/* ── has the registry arrived at all? ────────────────────────────────────────
   document.fonts.check() answers "can this text be drawn", NOT "is our face
   loaded". With no @font-face rule for the family it says YES — of a font we
   bundle, of an upload not yet registered, of a name nobody has ever heard of.
   Measured on a cold load: for the first ~550 ms document.fonts is EMPTY, since
   the faces arrive with styles/fonts.css, an @import chain that lands late —
   and through that whole window check() answers yes to every family there is.

   That is not a cosmetic race. buildText and buildTextArea only ASK for a load
   when fontReady says no, so type traced inside the window is drawn with a
   fallback and has no rebuild scheduled: the wrong letterforms are extruded and
   they stay, until something unrelated happens to rebuild the object. Reported
   as fonts that are "not always loading correctly" for every text layer, on a
   Mac and not here — the same window, opened wider by a slower stylesheet.

   So the first question is whether the registry exists yet. Until it does,
   nothing is ready and every builder gets the rebuild it was promised. */
let facesArrived = document.fonts.size > 0;
const waiting = [];
if (!facesArrived) {
  const t0 = performance.now();
  const poll = () => {
    /* Capped, so a page served without the stylesheet — a stripped build, a
       test harness — degrades to the old behaviour rather than never drawing
       type at all. */
    if (document.fonts.size === 0 && performance.now() - t0 < 10000) {
      requestAnimationFrame(poll); return;
    }
    facesArrived = true;
    while (waiting.length) waiting.shift()();
  };
  requestAnimationFrame(poll);
}

/* Families we have actually finished asking document.fonts to load, keyed
   family|weight. Recorded whether or not the load found anything: the question
   it answers is "have we asked and got an answer", not "did it work" — a family
   nobody ships must settle too, or a builder would rebuild on every frame
   waiting for a face that is never coming.

   Uploaded faces are added by registerFace instead, since they are handed to
   document.fonts directly and never go through a load. */
const asked = new Set();
const ours = family => !!builtInWeights(family) || uploaded.has(family);

export function fontReady(family, weight, text) {
  if (!facesArrived) return false;      // check() would say yes to anything

  /* OUR FACE, NOT A FACE. document.fonts.check() answers "can this text be
     drawn", and on a machine that has a font of the same name installed —
     which is most designers' Macs, for exactly the families a design tool
     bundles — the answer is yes long before OUR webfont has loaded. The build
     then traces the local face, whose letterforms are not the ones the export
     will use, and schedules no repair because nothing ever said it was not
     ready. Reported as a project opening in the wrong font and staying wrong
     until the font was picked again by hand, which is simply the first thing
     that forces a load.

     So for a family we ship or the document brought with it, having asked for
     it is a precondition. check() still decides the rest, because it is the
     only thing that knows about unicode-range subsets — a face loaded for
     Latin is genuinely not ready for the first ä. */
  if (ours(family) && !asked.has(family + '|' + weight)) return false;

  try { return document.fonts.check(matchSpec(family, weight), text || 'A'); }
  catch { return true; }        // malformed spec — never block a build on it
}

/* In-flight loads, so a burst of rebuilds does not fire a request per frame.
   Cleared on settle: if a later string needs a subset still missing, fontReady
   says so again and a fresh load starts. */
const pending = new Map();

/**
 * Load the face, then call back. Returns immediately.
 *
 * @param {string} family
 * @param {number|string} weight
 * @param {string} text            the copy to be traced, so the right subsets load
 * @param {() => void} [onReady]
 */
export function ensureFont(family, weight, text, onReady) {
  /* Asking now would resolve against an empty registry with no faces found,
     which reads as "this family does not exist" and warns about a font that is
     merely late. */
  if (!facesArrived) { waiting.push(() => ensureFont(family, weight, text, onReady)); return; }

  const key = family + '|' + weight;
  const inFlight = pending.get(key);
  if (inFlight) { if (onReady) inFlight.then(onReady, () => {}); return; }

  const pr = document.fonts.load(matchSpec(family, weight), text || 'A')
    .then(faces => {
      pending.delete(key);
      asked.add(key);
      /* Empty means nothing matched — a family we do not bundle and the user has
         not uploaded. Say so, rather than leaving the caller waiting on a rebuild
         that will trace a fallback anyway. */
      if (!faces.length) console.warn('[illusio] no face available for', family, weight);
      return faces;
    }, err => {
      /* Marked asked on failure too. The alternative is a builder that never
         stops rebuilding because the answer never arrives. */
      pending.delete(key);
      asked.add(key);
      console.warn('[illusio] font failed to load:', family, weight, err);
    });

  pending.set(key, pr);
  if (onReady) pr.then(onReady, () => {});
}

/** Promise form, for the load path that must finish before a document is applied. */
export const loadFont = (family, weight, text) =>
  new Promise(res => ensureFont(family, weight, text, res));

/** Warm the default so the very first text object is not traced from a fallback. */
export const preloadDefault = () => ensureFont(FALLBACK_FAMILY, 900, 'ABC');
